'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import type { BreadcrumbsProps } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { useState, type PropsWithChildren, useRef, useEffect } from 'react';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type PageHeaderProps = {
  heading?: string;
  /** The route owns the primary H1 by default; reusable/secondary headers must opt out. */
  headingRole?: 'primary' | 'section' | 'presentation';
  description?: string;
  headerLink?: React.ReactNode;
  mobileAction?: React.ReactNode;
  beforeDescription?: React.ReactNode;
  afterHeading?: React.ReactNode;
  afterDescription?: React.ReactNode;
  variant?: 'default' | 'center';

  className?: {
    wrapper?: string;
    container?: string;
    content?: string;
    header?: string;
    description?: string;
    breadcrumbs?: BreadcrumbsProps['className'];
    heading_row?: string;
  };
  breadcrumb?: Breadcrumb;
};

export function PageHeader({ variant, ...props }: PropsWithChildren<PageHeaderProps>) {
  const { t } = useTranslations();
  const [isExtended, setShowMore] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  // NEW: whether the description contains the wysiwyg root div
  const [hasWysiwyg, setHasWysiwyg] = useState(false);

  const descriptionRef = useRef<HTMLDivElement>(null);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const HeadingTag =
    props.headingRole === 'section'
      ? 'h2'
      : props.headingRole === 'presentation'
        ? 'div'
        : 'h1';

  useEffect(() => {
    if (templateSettings.category?.category_description_always_open?.enabled) {
      setShowMore(true);
      setShowToggle(false);
      return;
    }

    const checkDescriptionLines = () => {
      const descriptionElement = descriptionRef.current;
      if (!descriptionElement) return;

      // Detect the wysiwyg container (either the ref node itself or any descendant)
      const wysiwygExists =
        descriptionElement.id === 'npx_sp_wysiwyg' ||
        !!descriptionElement.querySelector('#npx_sp_wysiwyg');

      setHasWysiwyg(wysiwygExists);

      // Only compute/show the "show more" toggle when wysiwyg is NOT present
      if (wysiwygExists) {
        setShowToggle(false);
        return;
      }

      const style = window.getComputedStyle(descriptionElement);
      const lineHeight = parseInt(style.lineHeight, 10) || parseInt(style.fontSize, 10) * 1.2;
      const lines = descriptionElement.scrollHeight / lineHeight;

      setShowToggle(lines > 6);
    };

    checkDescriptionLines();

    window.addEventListener('resize', checkDescriptionLines);
    return () => window.removeEventListener('resize', checkDescriptionLines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.description]);

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-none bg-background py-0 sm:rounded-lg sm:pt-4',
        (props.breadcrumb && props.heading) ||
          (props.breadcrumb && props.description) ||
          (props.breadcrumb && props.children)
          ? 'gap-4'
          : '',
        'ea-homepagepage-header',
        templateSettings?.realty_header && 'bg-transparent',
        props.className?.wrapper,
      )}
    >
      {props.breadcrumb && (
        <Breadcrumbs
          className={{
            ...props.className,
            wrapper: cn(
              'mx-auto w-full max-w-screen-xl',
              'ea-homepage-header__breadcrumbs',
              templateSettings?.jobportal_header && 'bg-transparent',
              props.className?.wrapper,
            ),
          }}
          breadcrumb={props.breadcrumb}
        />
      )}

      <Container
        size="xl"
        className={cn(
          'flex flex-col rounded-none',
          'ea-homepage-header__container',
          {
            'items-center': variant === 'center',
            'items-start': variant === 'default',
          },
          props.className?.container,
        )}
      >
        {props.children}

        <div
          className={cn(
            'flex w-full flex-col gap-4',
            'ea-homepage-header__content',
            props.className?.content,
          )}
        >
          <div
            className={cn(
              'flex flex-row items-center  justify-between gap-4',
              'ea-homepage-header__heading-row',
              props.className?.heading_row,
            )}
          >
            {props.heading && (
              <div className="flex flex-row gap-2">
                <HeadingTag
                  {...(props.headingRole === 'primary' || !props.headingRole
                    ? { 'data-ea-primary-heading': true }
                    : {})}
                  className={cn(
                    'text-[32px] font-bold',
                    'ea-homepage-header__heading',
                    props.className?.header,
                  )}
                >
                  {props.heading}
                </HeadingTag>
                {props.afterHeading && (
                  <div className="flex flex-row items-center justify-end gap-2">
                    {props.afterHeading}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <div className="flex flex-row">
                <Button asChild variant="link" className="px-0">
                  {props.headerLink}
                </Button>
                <div className="flex flex-row items-center justify-end md:hidden">
                  {props.mobileAction}
                </div>
              </div>
            </div>
          </div>

          {props.description && (
            <div className="flex w-full flex-col items-start">
              {props.beforeDescription && (
                <div className="flex w-full flex-row items-center justify-end gap-2">
                  {props.beforeDescription}
                </div>
              )}

              <HtmlRenderer
                refDiv={descriptionRef}
                className={cn(
                  'h-auto min-h-min w-full overflow-hidden text-justify text-sm text-muted-foreground transition-all duration-300 ease-in-out',
                  !isExtended && !hasWysiwyg ? 'line-clamp-6' : 'line-clamp-none',
                  'ea-homepage-header__description',
                  props.className?.description,
                )}
                html={props.description}
              />
              {/*
              {showToggle && (
                <div className="flex w-full justify-center sm:hidden">
                  <Button
                    variant="link"
                    className="flex px-0 sm:hidden"
                    onClick={() => setShowMore(prev => !prev)}
                    type="button"
                  >
                    {isExtended ? t('text528') : t('text529')}
                  </Button>
                </div>
              )} */}

              {props.afterDescription && (
                <div className="flex flex-row items-center justify-end gap-2">
                  {props.afterDescription}
                </div>
              )}
            </div>
          )}
        </div>

        {/* CHANGE: hide this mt-5 button row when wysiwyg exists */}
        {!hasWysiwyg && (
          <div className="mt-5 flex w-full items-center justify-start">
            {showToggle && (
              <Button
                variant="dark"
                className="flex h-[39px] rounded-[10px] bg-[#111827] px-[18px] font-bold shadow-none hover:bg-[#111827]"
                onClick={() => setShowMore(prev => !prev)}
                type="button"
              >
                {isExtended ? t('text604') : t('text338')}
              </Button>
            )}
          </div>
        )}
      </Container>
    </div>
  );
}
